import { useState } from "react";
import { Loader2, Inbox } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";

const TONES = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  red: "bg-red-50 text-red-700 border-red-200",
  blue: "bg-sky-50 text-sky-700 border-sky-200",
  slate: "bg-slate-100 text-slate-600 border-slate-200",
};
const MAP = {
  green: ["Verified", "Active", "Registration Open", "Approved", "Paid", "Completed", "Enrolled", "Published", "Active Student", "Alumni", "sent", "active", "true"],
  amber: ["Pending Review", "Upcoming", "Waitlist", "Under Review", "Uploaded", "Submitted", "Pending", "In Progress", "Waiting", "Scheduled", "Started", "Application Started", "Application Submitted", "Enrollment Pending", "Partial", "Medium"],
  red: ["Full", "Rejected", "Resubmit Required", "Required", "Documents Required", "Overdue", "Registration Closed", "Failed", "failed", "Lost", "Not Qualified", "Withdrawn", "Cancelled", "High", "Urgent", "suspended", "Refunded"],
  blue: ["Needs More Information", "New Lead", "Contacted", "Interested", "To Do", "Draft", "Deferred", "Waitlisted"],
};
export function StatusBadge({ status, testid }) {
  const s = String(status ?? "");
  const tone = Object.keys(MAP).find((k) => MAP[k].includes(s)) || "slate";
  return (
    <span data-testid={testid || "status-badge"} className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONES[tone]}`}>
      {s || "—"}
    </span>
  );
}

export function EmptyState({ children, icon: Icon = Inbox, testid = "empty-state" }) {
  return (
    <div data-testid={testid} className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-10 text-center text-sm text-slate-500">
      <Icon className="h-6 w-6 text-slate-300" aria-hidden />
      <p className="max-w-md">{children}</p>
    </div>
  );
}

export const Spinner = ({ label = "Loading" }) => (
  <div className="flex items-center justify-center gap-2 py-12 text-slate-400" role="status" data-testid="loading-spinner">
    <Loader2 className="h-5 w-5 animate-spin" /> <span className="text-sm">{label}…</span>
  </div>
);

export function Confirm({ trigger, title = "Are you sure?", description, confirmLabel = "Confirm", onConfirm, testid = "confirm-dialog-action" }) {
  const [busy, setBusy] = useState(false);
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-dialog-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid={testid} disabled={busy} className="bg-red-600 hover:bg-red-700"
            onClick={async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } }}>
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function StatCard({ label, value, icon: Icon, tone = "slate", testid }) {
  const tones = { red: "bg-red-50 text-red-600", green: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600", slate: "bg-slate-100 text-slate-700", blue: "bg-sky-50 text-sky-600" };
  return (
    <div data-testid={testid} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        <p className="mt-1 font-heading text-2xl font-extrabold text-slate-900">{value}</p>
      </div>
      {Icon && <span className={`grid h-11 w-11 place-items-center rounded-xl ${tones[tone]}`}><Icon className="h-5 w-5" /></span>}
    </div>
  );
}

export const Field = ({ label, children, hint, required }) => (
  <label className="block space-y-1.5">
    <span className="text-sm font-semibold text-slate-700">{label}{required && <span className="text-red-600"> *</span>}</span>
    {children}
    {hint && <span className="block text-xs text-slate-500">{hint}</span>}
  </label>
);

export const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition-colors focus:border-red-500 focus:ring-2 focus:ring-red-100";

export function BarList({ data = {}, testid }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map((e) => e[1]));
  if (!entries.length) return <p className="py-4 text-sm text-slate-400">No data yet.</p>;
  return (
    <div className="space-y-2.5" data-testid={testid}>
      {entries.map(([k, v]) => (
        <div key={k}>
          <div className="mb-1 flex justify-between text-xs"><span className="font-medium text-slate-700">{k}</span><span className="font-bold text-slate-900">{v}</span></div>
          <div className="h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-red-500" style={{ width: `${(v / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

export function Panel({ title, action, children, className = "", testid }) {
  return (
    <section data-testid={testid} className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}>
      {(title || action) && (
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="font-heading text-base font-bold text-slate-900">{title}</h2>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}
